import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human labels for each owner-panel section (matches the tab ids used in OwnerPanel).
const SECTION_LABELS = {
  dashboard: 'Dashboard', earnings: 'Platform Earnings', health: 'System Health',
  company: 'Company Details', config: 'Configuration', payments: 'Payment Methods',
  users: 'User Management', announcements: 'Announcements', admins: 'Administrators',
  verification: 'ID Verification', moderation: 'Reports & Moderation', jobs: 'Job Monitoring',
  disputes: 'Disputes', activity: 'Activity Logs', support: 'Support', code: 'Code Editor',
  database: 'Database', reset: 'Reset Data',
};

const AdminPermissionsModal = ({ admin, onClose }) => {
  const [sections, setSections] = useState([]);
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/owner/admins`);
        if (!active) return;
        const secs = r.data.sections || [];
        const me = (r.data.admins || []).find((a) => a.id === admin.id);
        setSections(secs);
        const base = {};
        secs.forEach((s) => {
          const p = me?.permissions?.[s];
          base[s] = { access: p ? !!p.access : true, level: p?.level === 'view' ? 'view' : 'edit' };
        });
        setPerms(base);
      } catch (e) { toast.error('Failed to load permissions'); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [admin.id]);

  const toggleAccess = (s) => setPerms((p) => ({ ...p, [s]: { ...p[s], access: !p[s].access } }));
  const setLevel = (s, level) => setPerms((p) => ({ ...p, [s]: { ...p[s], level } }));
  const setAll = (access) => setPerms((p) => { const n = {}; sections.forEach((s) => { n[s] = { access, level: p[s]?.level || 'edit' }; }); return n; });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/admins/${admin.id}/permissions`, { permissions: perms });
      toast.success('Permissions saved');
      onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="admin-permissions-modal">
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="px-5 py-3 border-b border-gray-700 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-sky-400" />
            <div>
              <h3 className="font-bold text-gray-100">Panel Access</h3>
              <p className="text-xs text-gray-400">{admin.name} · {admin.email}</p>
            </div>
          </div>
          <button onClick={() => onClose(false)} className="text-gray-400 hover:text-white" data-testid="admin-permissions-close"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div>
        ) : (
          <>
            <div className="px-5 py-2 border-b border-gray-700 flex gap-3 text-xs shrink-0">
              <button onClick={() => setAll(true)} className="text-sky-300 hover:text-sky-200" data-testid="perm-grant-all">Grant all</button>
              <button onClick={() => setAll(false)} className="text-red-300 hover:text-red-200" data-testid="perm-revoke-all">Revoke all</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-1">
              {sections.map((s) => (
                <div key={s} className="flex items-center justify-between gap-3 py-2 border-b border-gray-700/50" data-testid={`perm-row-${s}`}>
                  <span className="text-sm text-gray-200 flex-1">{SECTION_LABELS[s] || s}</span>
                  {/* View / Edit segmented control */}
                  <div className={`flex rounded-lg overflow-hidden border border-gray-600 ${perms[s].access ? '' : 'opacity-40 pointer-events-none'}`}>
                    <button onClick={() => setLevel(s, 'view')} className={`px-3 py-1 text-xs ${perms[s].level === 'view' ? 'bg-slate-600 text-white' : 'text-gray-400'}`} data-testid={`perm-view-${s}`}>View</button>
                    <button onClick={() => setLevel(s, 'edit')} className={`px-3 py-1 text-xs ${perms[s].level === 'edit' ? 'bg-orange-500 text-white' : 'text-gray-400'}`} data-testid={`perm-edit-${s}`}>Edit</button>
                  </div>
                  {/* Access toggle */}
                  <button
                    onClick={() => toggleAccess(s)}
                    role="switch"
                    aria-checked={perms[s].access}
                    className={`relative w-11 h-6 rounded-full transition-colors ${perms[s].access ? 'bg-green-500' : 'bg-gray-600'}`}
                    data-testid={`perm-access-${s}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${perms[s].access ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-gray-700 flex gap-3 shrink-0">
              <button onClick={() => onClose(false)} className="flex-1 border border-gray-600 text-gray-300 py-2 rounded-lg font-semibold hover:bg-gray-700" data-testid="admin-permissions-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="flex-1 bg-orange-500 text-white py-2 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="admin-permissions-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Permissions
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPermissionsModal;
